import { useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useBlobUrl } from "@/hooks/useBlobUrl";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, Trash2, Loader2, AlertTriangle, MapPin, GraduationCap, Lock,
  RefreshCw, UploadCloud, Video, X, Film, Unlock,
} from "lucide-react";
import { toast } from "sonner";

export const VideoCard = ({ video, onDeleted, onChanged, showMr }) => {
  const [deleting, setDeleting] = useState(false);
  const [reDialog, setReDialog] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const locked = video.locked || (video.change_count ?? 0) >= 2;
  const remaining = 2 - (video.change_count ?? 0);

  const [downloading, setDownloading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const blobUrl = useBlobUrl(
    `/videos/${video.id}/stream`,
    video.status === "completed" && !video.playback_url,
  );
  const streamUrl = video.playback_url || blobUrl;

  const handleResetChanges = async () => {
    if (!window.confirm(`Reset the change limit for ${video.doctor_name}? They'll be able to re-upload again.`)) return;
    setResetting(true);
    try {
      await api.post(`/videos/${video.id}/reset-changes`);
      toast.success("Change limit reset");
      onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setResetting(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    const filename = `YogaDay_${(video.doctor_name || "doctor").replace(/\s+/g, "_")}.mp4`;
    try {
      let blob;
      if (video.playback_url) {
        const res = await fetch(video.playback_url);
        if (!res.ok) throw new Error("Download failed");
        blob = await res.blob();
      } else {
        const res = await api.get(`/videos/${video.id}/download`, {
          responseType: "blob",
          withCredentials: false,
        });
        blob = res.data;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e, "Download failed")); }
    finally { setDownloading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this video permanently?")) return;
    setDeleting(true);
    try {
      await api.delete(`/videos/${video.id}`);
      toast.success("Video deleted");
      onDeleted?.(video.id);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDeleting(false); }
  };

  const pick = (f) => {
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (![".mp4", ".mov", ".webm"].includes(ext)) { toast.error("Video must be MP4, MOV or WEBM"); return; }
    setFile(f);
  };

  const submitReupload = async () => {
    if (!file) { toast.error("Add a new video first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("source_kind", "upload");
    fd.append("file", file);
    try {
      await api.post(`/videos/${video.id}/reupload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Re-uploaded! Regenerating video.");
      setReDialog(false); setFile(null);
      onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`video-card-${video.id}`} className="group rounded-lg border border-stone-200 bg-white overflow-hidden transition-all hover:shadow-md hover:border-stone-300">
      <div className="relative aspect-[9/16] bg-stone-900 max-h-[420px]">
        {video.status === "completed" ? (
          streamUrl ? (
            <video data-testid={`video-player-${video.id}`} src={streamUrl} controls playsInline className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-stone-300">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
            </div>
          )
        ) : video.status === "failed" ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-red-200">
            <AlertTriangle className="h-8 w-8" />
            <p className="text-xs text-red-100/80">{video.error || "Rendering failed"}</p>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-stone-300">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
            <p className="text-sm">Merging video…</p>
          </div>
        )}
        {video.template_name && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur">
            <Film className="h-3 w-3" /> {video.template_name}
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h4 className="font-heading font-semibold text-stone-900 truncate">{video.doctor_name}</h4>
            <p className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3" /> {video.place || video.doctor_city || "—"}
            </p>
          </div>
          <StatusBadge status={video.status} />
        </div>
        {(video.qualification || video.doctor_specialty) && (
          <p className="text-xs text-stone-500 flex items-center gap-1">
            <GraduationCap className="h-3 w-3" /> {video.qualification || video.doctor_specialty}
          </p>
        )}
        {showMr && <p className="text-xs text-stone-400">By {video.mr_name} · {video.region || "—"}</p>}

        {locked && (
          <div className="flex items-center justify-between text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-stone-200 px-2 py-0.5 font-semibold text-stone-600"><Lock className="h-3 w-3" /> Locked (2/2 changes)</span>
            {showMr && (
              <button onClick={handleResetChanges} disabled={resetting} data-testid={`reset-changes-${video.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlock className="h-3 w-3" />} Reset limit
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <button onClick={handleDownload} disabled={video.status !== "completed" || downloading} data-testid={`download-button-${video.id}`}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download
          </button>
          <button onClick={() => setReDialog(true)} disabled={locked} title={locked ? "Change limit reached" : "Re-upload"} data-testid={`reupload-button-${video.id}`}
            className="inline-flex items-center justify-center rounded-md border border-stone-200 px-3 py-2 text-stone-500 transition-colors hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 disabled:opacity-40 disabled:cursor-not-allowed">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={handleDelete} disabled={deleting} data-testid={`delete-button-${video.id}`}
            className="inline-flex items-center justify-center rounded-md border border-stone-200 px-3 py-2 text-stone-500 transition-colors hover:bg-red-50 hover:text-red-600 hover:border-red-200">
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Re-upload dialog */}
      <Dialog open={reDialog} onOpenChange={(o) => { if (!o && !busy) { setReDialog(false); setFile(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid={`reupload-dialog-${video.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading">Re-upload video</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500">Replace the video for <strong>{video.doctor_name}</strong>. {remaining} change{remaining !== 1 ? "s" : ""} remaining.</p>
          <div onClick={() => inputRef.current?.click()} className="flex flex-col items-center justify-center min-h-[150px] rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 p-6 text-center cursor-pointer hover:bg-stone-100">
            <input ref={inputRef} type="file" accept=".mp4,.mov,.webm" className="hidden" data-testid={`reupload-file-${video.id}`} onChange={(e) => pick(e.target.files?.[0])} />
            {file ? (
              <div className="flex flex-col items-center gap-1"><Video className="h-7 w-7 text-emerald-600" /><p className="text-sm font-medium">{file.name}</p>
                <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-xs text-stone-500 hover:text-red-600 inline-flex items-center gap-1"><X className="h-3 w-3" /> Remove</button></div>
            ) : (<><UploadCloud className="h-7 w-7 text-emerald-600" /><p className="mt-2 text-sm font-medium text-stone-700">Select new video</p></>)}
          </div>
          <DialogFooter>
            <button onClick={() => { setReDialog(false); setFile(null); }} disabled={busy} className="rounded-md border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">Cancel</button>
            <button onClick={submitReupload} disabled={busy || !file} data-testid={`reupload-submit-${video.id}`}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Re-upload
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
